import { faCircleX, faCloudExclamation, faWandMagicSparkles } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useSalesBOMTimeoutHandler } from '@hooks/useSalesBOMTimeoutHandler';
import { Stack } from '@mui/material';
import { FormFooter } from '@pages/CreateNewProductPage/CreateProductForm/CreateProductFormFooter';
import { OutlinedButton } from '@pages/UploadPage/styles';
import { FileUploader } from '@partful/features/file-uploader/FileUploader';
import { WizardErrorPage } from '@partful/features/instructions/ui/WizardErrorPage';
import { Step } from '@partful/pages/create-product/ui/Step';
import { classNames } from '@partful/shared/lib/classNames';
import { useUploadFile } from '@partful/shared/lib/hooks/useUploadFile';
import { WizardPageContent, WizardPageFooter, WizardPageLayout } from '@partful/shared/ui/layouts/WizardPageLayout';
import { LoadingMessage } from '@partful/shared/ui/LoadingMessage/LoadingMessage';
import { type SalesBomJob } from '@providers/job/types';
import { useTenant } from '@providers/Tenant';
import { productInventoryEndpoints } from '@services/productInventory';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Redirect } from 'react-router';
import {
  useCancelSBOMJob,
  useCreateSalesBOMJob,
  useSalesBOMForm,
  useSalesBOMPageLinks,
  useSalesBOMPostIngest,
} from 'src/v2/pages/create-product/sales-bom/hooks';
import { ErrorList } from 'src/v2/pages/create-product/sales-bom/ui/ErrorList/ErrorList';
import { useInvalidateQueries } from 'src/v2/shared/lib/hooks/useInvalidateQueries';
import { useProductInventoryQuery } from 'src/v2/shared/lib/hooks/useProductInventoryQuery';
import { useQueryParams } from 'src/v2/shared/lib/hooks/useQueryParams';
import { Button } from 'src/v2/shared/ui/Button';
import { SalesBOMFormSchema } from './lib/SalesBOMFormSchema';
import styles from './SalesBOM.module.scss';
import { DownloadSalesBOM } from './ui/DownloadSalesBOM/DownloadSalesBOM';

const REFETCH_JOB_INTERVAL = 10000;

export function SalesBOM() {
  const { t } = useTranslation('createProduct');
  const { accountId } = useTenant();

  const [ignoreFailedJob, setIgnoreFailedJob] = useState<string | undefined>();

  const { configurationPID, productName, runID } = useQueryParams();
  const { invalidateContentProcessingJobsQueries } = useInvalidateQueries();

  const { data: jobs, isLoading: isDeterminingInitialJobStatus } = useProductInventoryQuery<SalesBomJob[]>(
    productInventoryEndpoints.contentProcessing.root,
    {
      account: accountId,
      jobType: 'SalesBOM',
      configurationPID,
    }
  );

  const salesBOMJobData = jobs?.[0];

  // if there is an existing in progress job, then start polling this job
  const inProgressSalesBOMJobRunID = salesBOMJobData?.status === 'IN_PROGRESS' ? salesBOMJobData?.runID : null;
  const { data: inProgressSalesBOMJobData } = useProductInventoryQuery<SalesBomJob>(
    `${productInventoryEndpoints.contentProcessing.sbom}/${inProgressSalesBOMJobRunID}`,
    { account: accountId },
    {
      enabled: Boolean(inProgressSalesBOMJobRunID),
      refetchInterval: REFETCH_JOB_INTERVAL,
      notifyOnChangeProps: 'all',
    }
  );

  // periodically check if this in-progress jobs has timed out so the page isn't waiting forever
  useSalesBOMTimeoutHandler(inProgressSalesBOMJobData, accountId);

  const { cancelJob: cancelInProgressJob } = useCancelSBOMJob(accountId, salesBOMJobData?.runID);
  const {
    mutateAsync: createSalesBOMJob,
    isError: isCreateJobError,
    reset: resetCreateSalesBOMJob,
  } = useCreateSalesBOMJob(accountId, configurationPID);
  const { mutateAsync: uploadFile, isError: isUploadError, reset: resetFileUpload } = useUploadFile();
  const {
    mutateAsync: postSBOMIngest,
    isError: isPostIngestError,
    reset: resetPostIngest,
  } = useSalesBOMPostIngest(accountId, runID);

  const isUploadSequenceError = isCreateJobError || isUploadError || isPostIngestError;

  const onSubmit = useCallback(
    async (data: SalesBOMFormSchema): Promise<void> => {
      const { file } = data;

      const { uploadURL, runID: sbomRunID } = await createSalesBOMJob();

      try {
        await uploadFile({ file, uploadURL });
        await postSBOMIngest(sbomRunID);
        await invalidateContentProcessingJobsQueries();
      } catch (error) {
        // We catch the error to prevent it bubbling up
        // the isError attributes returned from the hooks will be used to determine the error state
      }
    },
    [createSalesBOMJob, invalidateContentProcessingJobsQueries, uploadFile, postSBOMIngest]
  );

  const { resetForm } = useSalesBOMForm();

  /* istanbul ignore next - this will be covered by e2e tests as integration tests will take too long to run since inProgressSalesBOMJobData is a polling request */
  if (['FAILED', 'COMPLETED'].includes(inProgressSalesBOMJobData?.status ?? '')) {
    invalidateContentProcessingJobsQueries();
  }

  const isComplete = salesBOMJobData?.status === 'COMPLETED';

  const resetUploadSequence = () => {
    resetCreateSalesBOMJob();
    resetFileUpload();
    resetPostIngest();
  };

  const cancelJob = async () => {
    await cancelInProgressJob();
    resetForm();
    await invalidateContentProcessingJobsQueries();
  };

  const retryUpload = async () => {
    resetForm();
    await cancelInProgressJob();
    await invalidateContentProcessingJobsQueries();
    resetUploadSequence();
    setIgnoreFailedJob(salesBOMJobData?.runID);
  };

  const { nextLink, previousLink } = useSalesBOMPageLinks(configurationPID, productName, salesBOMJobData, runID);

  if (isDeterminingInitialJobStatus) {
    return null;
  }

  if ((salesBOMJobData?.status === 'FAILED' && ignoreFailedJob !== salesBOMJobData?.runID) || isUploadSequenceError) {
    const errorTitle = (
      <>
        <FontAwesomeIcon icon={faWandMagicSparkles} /> {t('sbom.form.failure.errorProcessingSBOM')}{' '}
      </>
    );
    return (
      <WizardErrorPage
        errorIcon={<FontAwesomeIcon icon={faCloudExclamation} />}
        errorTitle={errorTitle}
        errorMessage={salesBOMJobData && <ErrorList runID={salesBOMJobData.runID} salesBOMJobData={salesBOMJobData} />}
        errorActions={
          <Button className={styles.uploadButton} onClick={() => retryUpload()}>
            {t('sbom.form.failure.uploadNewFile')}
          </Button>
        }
      />
    );
  }

  if (salesBOMJobData?.status === 'IN_PROGRESS' || inProgressSalesBOMJobData?.status === 'IN_PROGRESS') {
    return (
      <WizardPageLayout>
        <WizardPageContent>
          <Stack sx={{ alignItems: 'center' }}>
            <LoadingMessage
              title={t('sbom.form.processing.processingSBOMStatus')}
              message={t('freeToLeaveThisPage')}
              actions={
                <OutlinedButton
                  color="error"
                  endIcon={<FontAwesomeIcon icon={faCircleX} />}
                  onClick={() => cancelJob()}
                >
                  {t('common:cancel')}
                </OutlinedButton>
              }
            />
          </Stack>
        </WizardPageContent>
      </WizardPageLayout>
    );
  }

  if (isComplete) {
    return (
      <Redirect
        to={{
          pathname: `/create-product/match-report`,
          search: `?configurationPID=${configurationPID}&productName=${productName}&salesBOMJobRunID=${salesBOMJobData?.runID}&cadJobRunID=${runID}`,
          state: {
            successMessage: {
              title: t('fileUploaded'),
              body: t('sbom.success.processedSuccess'),
            },
          },
        }}
      />
    );
  }

  return (
    <WizardPageLayout>
      <WizardPageContent className={classNames(styles.container)}>
        <Step title={t('sbom.download.title')} position={2.1}>
          <DownloadSalesBOM />
        </Step>
        <Step title={t('sbom.upload.title')} required position={2.2}>
          <FileUploader submitHandler={onSubmit} />
        </Step>
      </WizardPageContent>
      <WizardPageFooter>
        <FormFooter
          buttonDisabled={!isComplete}
          buttonI18nText="nextStep"
          backButton
          nextStepURL={nextLink}
          previousStepURL={previousLink}
        />
      </WizardPageFooter>
    </WizardPageLayout>
  );
}
